import { InputAdornment, InputProps, TextField } from '@mui/material';
import { TextFieldProps } from '@src/assets/types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { PublishContentContext } from '../../modules/hierarchy/publish/PublishContentContext';

function TextFieldElement({
    control,
    id,
    name,
    label,
    labelId,
    sideEffect,
    type = 'text',
    inputLabelProps,
    defaultValue = '',
    measureFieldProps,
    readOnly = false,
    disabled = false,
    fullWidth = true,
    multiLine = false,
    setIsoCode,
    isEditNode,
    required = false,
    sxCustom = {},
    sxTheme,
    inputProps,
    helperText,
    readOnlyMessage,
    onKeyDown,
    validationRule = () => true,
    onChange = () => {},
    LanguageComponent,
    showAsPercentage = false,
    ...rest
}: Readonly<TextFieldProps & { InputProps?: InputProps; showAsPercentage?: boolean }>) {
    const { field } = useController({
        name,
        control,
        defaultValue,
    });

    const isPublishContent = useContext(PublishContentContext);

    const { watch, setValue, getValues } = useFormContext();

    const { unitFieldName, convertToUnit } = measureFieldProps || {};

    const { isoCode, fieldName, addIsoCode } = setIsoCode || {};

    const watchUnitField = watch(unitFieldName as string);

    const toDisplayValue = (val: string) =>
        showAsPercentage && val !== '' && !Number.isNaN(parseFloat(val))
            ? String(parseFloat(val) * 100)
            : val;

    const toStoredValue = (val: string) =>
        showAsPercentage && val !== '' && !Number.isNaN(parseFloat(val))
            ? String(parseFloat(val) / 100)
            : val;

    const [inputValue, setInputValue] = useState(toDisplayValue(field.value ?? ''));
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastFormValueRef = useRef<string>(field.value ?? '');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        // Update local state immediately for lag-free typing
        setInputValue(newValue);

        // Clear existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Debounce form value update
        debounceTimeoutRef.current = setTimeout(() => {
            const storedValue = toStoredValue(newValue);
            lastFormValueRef.current = storedValue;
            field.onChange(storedValue);
            onChange(storedValue);
        }, 300);
    };

    useEffect(() => {
        // required conversion function for the measure field
        if (watchUnitField && convertToUnit && unitFieldName) {
            const convertedValue = convertToUnit(field.value, watchUnitField).toString();
            // Clear any pending debounce to prevent overwriting the conversion
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }
            setInputValue(convertedValue);
            lastFormValueRef.current = convertedValue;
            field.onChange(convertedValue);
        }
    }, [watchUnitField]);

    useEffect(() => {
        if (isoCode && fieldName && addIsoCode) {
            if (!getValues(fieldName)) {
                setValue(fieldName, isoCode);
            }
        }
    }, [isoCode]);

    // Sync local value with field value when it changes externally (e.g., form reset)
    // Only sync if the change is external (not from our debounced update)
    useEffect(() => {
        const currentFieldValue = field.value ?? '';
        // Only sync if field value changed externally (different from what we last set)
        if (currentFieldValue !== lastFormValueRef.current && currentFieldValue !== inputValue) {
            // Clear any pending debounce to prevent overwriting the external change
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }
            setInputValue(toDisplayValue(currentFieldValue));
            lastFormValueRef.current = currentFieldValue;
        }
    }, [field.value, inputValue]);

    // Cleanup debounce timeout on unmount
    useEffect(
        () => () => {
            clearTimeout(debounceTimeoutRef.current ?? undefined);
        },
        [],
    );

    const isNumber = type === 'number';

    const handleWheel = isNumber
        ? (e: React.WheelEvent<HTMLInputElement>) => (e.target as HTMLInputElement).blur()
        : undefined;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (isNumber && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
            e.preventDefault();
            return;
        }
        onKeyDown?.(e);
    };

    const percentageAdornment = showAsPercentage ? (
        <InputAdornment position="end">%</InputAdornment>
    ) : null;

    const IProps = LanguageComponent
        ? {
              ...inputProps,
              endAdornment: <InputAdornment position="end">{LanguageComponent}</InputAdornment>,
          }
        : {
              ...rest.InputProps,
              ...(percentageAdornment && {
                  endAdornment: (
                      <>
                          {rest.InputProps?.endAdornment}
                          {percentageAdornment}
                      </>
                  ),
              }),
          };

    const isInvalid = !validationRule(field.value);
    const getResolvedHelperText = () => {
        if (isInvalid) {
            return helperText ?? 'invalid entry';
        }
        if (readOnly && readOnlyMessage) {
            return readOnlyMessage;
        }
        return '';
    };

    return (
        <TextField
            error={isInvalid}
            helperText={getResolvedHelperText()}
            id={id}
            label={label}
            InputLabelProps={inputLabelProps}
            fullWidth={fullWidth}
            type={type}
            onClick={() => (sideEffect ? sideEffect() : null)}
            onChange={handleChange} // send value to hook form
            value={inputValue} // input value
            name={field.name} // send down the input name
            inputRef={field.ref} // send input ref, so we can focus on input when error appear
            disabled={disabled || readOnly}
            required={required}
            multiline={multiLine}
            onKeyDown={handleKeyDown}
            onWheel={handleWheel}
            rows={8}
            sx={[
                theme => ({
                    margin: `${theme.spacing(1)} 0`,
                    minWidth: 230,
                    ...sxTheme?.(theme),
                }),
                ...(isNumber
                    ? [
                          {
                              '& input[type=number]': { MozAppearance: 'textfield' },
                              '& input[type=number]::-webkit-outer-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                              },
                              '& input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                              },
                          },
                      ]
                    : []),
                sxCustom,
            ]}
            {...rest}
            {...(isPublishContent && {
                variant: 'standard',
            })}
            InputProps={IProps}
        />
    );
}

export default TextFieldElement;
